import { RoleMapping, Permission } from '@ekkogmbh/apisdk';
import { alpha, Box, Button, Card, Chip, Collapse, Stack, Typography } from '@mui/material';
import { Apps, CheckCircle, ErrorOutline, ExpandLess, ExpandMore } from '@mui/icons-material';
import moment from 'moment';
import React, { useState } from 'react';

interface UserRoleCardProps {
  nodeRoleMapping: RoleMapping;
}

export const UserRoleCard = ({ nodeRoleMapping }: UserRoleCardProps): React.JSX.Element => {
  const [extended, setExtended] = useState<boolean>(false);

  const { role, coordinate, validUntil } = nodeRoleMapping;

  const isExpired = moment().isAfter(validUntil);
  const expirationText = moment(validUntil).fromNow();
  const initial = role.name?.[0]?.toUpperCase() ?? '?';

  return (
    <Card
      variant={'outlined'}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color 150ms, box-shadow 150ms',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: (theme) => theme.shadows[2],
        },
      }}
    >
      <Box sx={{ p: 2, flex: 1 }}>
        <Stack direction={'row'} spacing={1.5} alignItems={'flex-start'}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 1,
              flexShrink: 0,
              fontWeight: 700,
              fontSize: 18,
              color: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
            }}
          >
            {initial}
          </Box>
          <Stack direction={'column'} spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
            <Typography fontWeight={700} fontSize={16} noWrap>
              {role.name}
            </Typography>
            <Stack direction={'row'} spacing={0.5} alignItems={'center'} sx={{ color: 'text.secondary' }}>
              <Apps sx={{ fontSize: 16 }} />
              <Typography variant={'body2'} noWrap>
                {coordinate}
              </Typography>
            </Stack>
          </Stack>
          <Chip
            size={'small'}
            color={isExpired ? 'error' : 'success'}
            variant={isExpired ? 'filled' : 'outlined'}
            icon={isExpired ? <ErrorOutline /> : <CheckCircle />}
            label={isExpired ? 'Expired' : 'Active'}
            sx={{ fontWeight: 700, flexShrink: 0 }}
          />
        </Stack>
        <Typography
          variant={'caption'}
          sx={{ color: isExpired ? 'error.main' : 'text.secondary', mt: 1.5, display: 'block' }}
        >
          {isExpired ? 'Expired' : 'Expires'} {expirationText}
        </Typography>
      </Box>

      <Box sx={{ px: 2, pb: 1.5 }}>
        <Button
          size={'small'}
          fullWidth
          onClick={() => setExtended((prev) => !prev)}
          endIcon={extended ? <ExpandLess /> : <ExpandMore />}
          sx={{ justifyContent: 'space-between', textTransform: 'none' }}
        >
          {role.permissions.length} {role.permissions.length === 1 ? 'permission' : 'permissions'}
        </Button>
        <Collapse in={extended} timeout={'auto'} unmountOnExit>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pt: 1 }}>
            {role.permissions.map((permission: Permission, index: number) => (
              <Chip key={index} label={permission} size={'small'} variant={'outlined'} />
            ))}
          </Box>
        </Collapse>
      </Box>
    </Card>
  );
};
